import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Banner, BannerModesEnum, Text } from '@taxdown/piccolo-web';
import { Card, CardClassify } from '@taxdown/kami-sama/dist/kamiSama';
import { showModalAction } from 'Store/modules/app/actions';
import KamiContext from 'Context/KamiContext';
import useScreen, { ScreenClassifyType } from 'Hooks/useScreen';
import FormContainer from 'Components/UI/FormContainer';
import ContentContainer from 'Components/UI/ContentContainer';
import HeaderInfo from 'Components/HeaderInfo';
import { WARNING_MODAL } from 'Constants/modals';
import { ScreenPropsType } from 'Types/common';
import ValidationSubmit from '../ValidationSubmit';
import ClassifyCard from '../SummaryScreen/components/ClassifyCard';
import HelpInfo from '../HelpInfo';
import * as Styled from '../../styles';

const SummaryClassificator = ({ screenName }: ScreenPropsType): JSX.Element => {
  const { screen, renderForm, onNextScreen, onPrevScreen } = useScreen(
    screenName
  ) as ScreenClassifyType;
  const { saveData } = useContext(KamiContext);

  const { header, subheader, help, bannerData, helpButtonUrl } = renderForm;
  const [classifyCards, setClassifyCards] = useState(screen.getCards());
  const dispatch = useDispatch();

  useEffect(() => {
    saveData({ stage: screen.getSection() });
  }, []);

  const onUpdate = (): void => {
    setClassifyCards(screen.getCards());
  };

  const onDelete = (childCard: Card): void => {
    screen.delete(childCard);
    onUpdate();
    saveData({ stage: screen.getSection() });
  };

  const deleteCard = (childCard): void => {
    const modalOptions = { type: WARNING_MODAL, action: () => onDelete(childCard) };
    const { warningModalTitle, warningModalContent } = childCard.cardManager.cardGroupForm;
    dispatch(
      showModalAction({
        title: warningModalTitle,
        content: warningModalContent,
        options: modalOptions,
      })
    );
  };

  return (
    <ContentContainer testID="classify-section">
      <Styled.Container direction="column" fullWidth fullHeight gap="dimension_16">
        <FormContainer>
          <HeaderInfo info={helpButtonUrl} tag="h2" mb="dimension_32">
            {header}
          </HeaderInfo>
          {subheader && (
            <Text type="paragraphs" color="grey" size="S_S_M" mb="dimension_32">
              {subheader}
            </Text>
          )}
          {bannerData && (
            <Banner
              id="summary-banner"
              mode={bannerData?.type as BannerModesEnum}
              label={bannerData?.body}
              {...bannerData}
              mb="dimension_32"
            />
          )}
          {classifyCards.map(classifyCard => (
            <ClassifyCard
              key={classifyCard.getIndex()}
              classifyCard={classifyCard as CardClassify}
              deleteCard={deleteCard}
            />
          ))}
        </FormContainer>
        {help && <HelpInfo info={help} />}
        <ValidationSubmit
          testID="btnSubmit"
          disabled={!screen.isDataValid()}
          onNextScreen={onNextScreen}
          onPrevScreen={onPrevScreen}
        />
      </Styled.Container>
    </ContentContainer>
  );
};

export default SummaryClassificator;
